import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import { TrackedTextField } from "src/theme/StyledFields";
import { useEffect, useState } from "react";
import FormErrors, { hasError } from "../allauth/components/FormErrors";
import ApiClient from "../config/ApiClient";

/**
 * Reusable component for requesting one-time login links via email.
 * Can be used in page layout (direct form) or modal layout (button opens modal).
 * 
 * @param {Object} props
 * @param {string} [props.email] - Optional initial email value
 * @param {Function} [props.onSuccess] - Optional callback when link is sent successfully
 * @param {Function} [props.onError] - Optional callback when error occurs
 * @param {string} [props.layout] - Layout mode: "page" (default) or "modal"
 * @param {boolean} [props.defaultOpen] - Open magic-link modal on mount
 * @param {boolean} [props.fullWidth] - Full-width trigger button (modal layout)
 * @param {string} [props.variant] - MUI Button variant for modal trigger
 * @param {string} [props.color] - MUI Button color for modal trigger
 * @param {React.ReactNode} [props.startIcon] - Optional start icon for modal trigger
 * @param {string} [props.dataAttr] - data-attr for analytics on modal trigger
 */
export default function OneTimeLoginLink({
    email: initialEmail = "",
    onSuccess,
    onError,
    layout = "modal",
    defaultOpen = false,
    fullWidth = false,
    variant = "outlined",
    color = "primary",
    startIcon,
    dataAttr,
}) {
    const [email, setEmail] = useState(initialEmail);
    useEffect(() => {
        setEmail(initialEmail);
    }, [initialEmail]);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(defaultOpen);

    const handleSubmit = async () => {
        if (!email || email.indexOf("@") < 1) {
            setError({ errors: [{ code: "invalid_email", message: "Please enter a valid email address." }] });
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            // Capture current URL as redirect URL
            let redirectUrl = window.location.pathname;
            if (redirectUrl === '/account/login') {
                redirectUrl = '/';
            }
            redirectUrl = redirectUrl + window.location.search;


            const response = await ApiClient.post("/api/auth/one-time-login-link", {
                email: email.trim(),
                redirect_url: redirectUrl || undefined,
            });

            if (response.success) {
                // Extract success message from response data
                const message = response.data?.detail || "Login link sent! Check your email.";
                setSuccessMessage(message);
                if (onSuccess) {
                    onSuccess(response.data);
                }
            } else {
                const errorData = {
                    errors: response.errors
                        ? Object.entries(response.errors).map(([key, value]) => ({
                            code: key,
                            message: Array.isArray(value) ? value.join(", ") : String(value),
                        }))
                        : [{ code: "request_failed", message: response.error || "Failed to send login link. Please try again." }],
                };
                setError(errorData);
                if (onError) {
                    onError(errorData);
                }
            }
        } catch (err) {
            const errorData = {
                errors: [{ code: "network_error", message: "Network error. Please check your connection and try again." }],
            };
            setError(errorData);
            if (onError) {
                onError(errorData);
            }
        } finally {
            setLoading(false);
        }
    };

    const formContent = (
        <>
            {successMessage ? (
                <Typography variant="body2" color="success.main" sx={{ textAlign: "center", py: 1 }}>
                    {successMessage}
                </Typography>
            ) : (
                <>
                    <TrackedTextField
                        fullWidth
                        size="small"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && email.indexOf("@") >= 1 && !loading) {
                                handleSubmit();
                            }
                        }}
                        type="email"
                        required
                        label="Email"
                        placeholder="Enter your email address"
                        disabled={loading}
                        error={hasError(error?.errors, "email")}
                        sx={{ mb: 1 }}
                    />
                    <FormErrors param="email" errors={error?.errors} />
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || email.indexOf("@") < 1}
                        variant="contained"
                        size="small"
                        fullWidth
                        sx={{ mt: 1 }}
                    >
                        {loading ? <CircularProgress size={16} color="inherit" /> : "Send Login Link"}
                    </Button>
                    <FormErrors errors={error?.errors} />
                </>
            )}
        </>
    );

    // Modal layout: show button that opens modal
    if (layout === "modal") {
        return (
            <Box sx={{ width: fullWidth ? "100%" : "auto", flex: fullWidth ? 1 : undefined, minWidth: 0 }}>
                <Button
                    type="button"
                    variant={variant}
                    color={color}
                    size={fullWidth ? "medium" : "small"}
                    fullWidth={fullWidth}
                    startIcon={startIcon}
                    onClick={() => {
                        setModalOpen(true);
                        setSuccessMessage(null);
                        setError(null);
                    }}
                    data-attr={dataAttr}
                >
                    Email me a login link
                </Button>
                <Dialog
                    open={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Request Login Link</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enter your email address and we'll send you a secure one-time login link.
                        </Typography>
                        {formContent}
                    </DialogContent>
                </Dialog>
            </Box>
        );
    }

    // Page layout: show form directly
    return formContent;

}
